import type { Raster } from "./render";

// A PDF made from the pages a document renders to (render.ts), the same pixels a POS printer gets: Arabic is shaped by
// the browser before it becomes pixels, so the PDF needs no fonts. Each page is one 1-bit image (1 = black), deflated
// when the browser can (CompressionStream), drawn at its physical size: width and height in dots ÷ dpi × 72 points.

async function deflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof CompressionStream === "undefined") return null;
  try {
    const out = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream("deflate"));
    return new Uint8Array(await new Response(out).arrayBuffer());
  } catch { return null; }
}

const ascii = (s: string) => new TextEncoder().encode(s);

/** A PDF of rendered pages; `title` goes in the document information. */
export async function rastersToPdf(pages: Raster[], dpi: number, title: string): Promise<Blob> {
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let size = 0;
  const push = (b: Uint8Array) => { parts.push(b); size += b.length; };
  const obj = (n: number, body: (Uint8Array | string)[]) => {
    offsets[n] = size;
    push(ascii(`${n} 0 obj\n`));
    for (const b of body) push(typeof b === "string" ? ascii(b) : b);
    push(ascii("\nendobj\n"));
  };
  push(ascii("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n"));
  // 1 catalog, 2 pages, 3 info; then per page: page, content, image.
  const kids = pages.map((_, i) => `${4 + i * 3} 0 R`).join(" ");
  obj(1, ["<< /Type /Catalog /Pages 2 0 R >>"]);
  obj(2, [`<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`]);
  const esc = (s: string) => [...s].map((ch) => (ch.charCodeAt(0) > 126 || ch.charCodeAt(0) < 32 ? "?" : ch)).join("").replace(/[()\\]/g, "\\$&");
  obj(3, [`<< /Title (${esc(title)}) /Producer (Delta) >>`]);
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i]!, n = 4 + i * 3;
    const w = (p.width / dpi) * 72, h = (p.height / dpi) * 72;
    obj(n, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w.toFixed(2)} ${h.toFixed(2)}] /Resources << /XObject << /Im0 ${n + 2} 0 R >> >> /Contents ${n + 1} 0 R >>`]);
    const content = `q ${w.toFixed(2)} 0 0 ${h.toFixed(2)} 0 0 cm /Im0 Do Q`;
    obj(n + 1, [`<< /Length ${content.length} >>\nstream\n${content}\nendstream`]);
    const raw = Uint8Array.from(atob(p.data), (ch) => ch.charCodeAt(0)), packed = await deflate(raw);
    const data = packed ?? raw;
    obj(n + 2, [`<< /Type /XObject /Subtype /Image /Width ${p.width} /Height ${p.height} /ColorSpace /DeviceGray /BitsPerComponent 1 /Decode [1 0]${packed ? " /Filter /FlateDecode" : ""} /Length ${data.length} >>\nstream\n`, data, "\nendstream"]);
  }
  const xref = size, count = 4 + pages.length * 3;
  let table = `xref\n0 ${count}\n0000000000 65535 f \n`;
  for (let n = 1; n < count; n++) table += `${String(offsets[n]).padStart(10, "0")} 00000 n \n`;
  push(ascii(`${table}trailer\n<< /Size ${count} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xref}\n%%EOF\n`));
  return new Blob(parts as BlobPart[], { type: "application/pdf" });
}

/** Saves a blob under a file name (the browser's download). */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob), a = document.createElement("a");
  a.href = url; a.download = name; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * The document the browser prints (printPages): one sheet per rendered page, each exactly one printed page.
 * - Page size: `paperMm` wide (the paper: 210 mm, or the 80 mm / 58 mm roll, the image keeping its printable width,
 *   centred) and, for a receipt, as long as its content. `@page { size: 80mm auto }` is not valid CSS: Firefox ignored
 *   it and printed a receipt on Letter paper, its end on a second page.
 * - Each sheet is a little shorter than the page and clips its overflow, and only sheets before the last force a
 *   break: an image exactly one page high spilled a fraction into the next page in Firefox, which printed blank pages.
 */
export function printSheetsHtml(images: { src: string; width: number; height: number }[], dpi: number, paperMm: number, heightMm: number | null): string {
  const mm = (dots: number) => (dots / dpi) * 25.4;
  const pageH = heightMm ?? Math.max(...images.map((p) => mm(p.height)));
  const sheets = images.map((p, i) => `<div class="sheet${i < images.length - 1 ? " brk" : ""}"><img src="${p.src}" alt="" style="width:${mm(p.width).toFixed(2)}mm;height:${mm(p.height).toFixed(2)}mm"></div>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Delta</title><style>
@page{size:${paperMm}mm ${pageH.toFixed(2)}mm;margin:0}
html,body{margin:0;padding:0;background:#fff}
.sheet{width:${paperMm}mm;height:${(pageH - 0.8).toFixed(2)}mm;overflow:hidden;display:flex;justify-content:center;align-items:flex-start;break-inside:avoid;page-break-inside:avoid}
.brk{break-after:page;page-break-after:always}
img{display:block;flex:none}
</style></head><body>${sheets}</body></html>`;
}

/**
 * Prints rendered pages through the browser's print dialog (the fallback when no POS printer is mapped), in a hidden
 * frame so the screen behind is not printed. The page size is requested in CSS; the printer's own paper and scale
 * settings still apply (docs/direct-printing.md, "Printing from the browser").
 */
export async function printPages(pages: Raster[], dpi: number, paperMm: number, heightMm: number | null, toCanvas: (r: Raster) => HTMLCanvasElement) {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;inset-inline-start:-9999px";
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(printSheetsHtml(pages.map((p) => ({ src: toCanvas(p).toDataURL("image/png"), width: p.width, height: p.height })), dpi, paperMm, heightMm));
  doc.close();
  await Promise.all([...doc.images].map((i) => i.decode().catch(() => undefined)));
  frame.contentWindow!.focus();
  frame.contentWindow!.print();
  setTimeout(() => frame.remove(), 60_000);
}
